/**
 * KpiAssignedAuditsDrawer.jsx
 *
 * Two modes, one component:
 *  - mode="unassigned"  → read-only browse of KPIs still needing the
 *                          current level. Search + KPI tag filter +
 *                          location filter only — no user filter, no
 *                          remove buttons, since there's nothing assigned
 *                          yet to act on. Prior-level assignments show as
 *                          small context chips only.
 *  - mode="assignments" → review/remove for levels already filled below
 *                          the current level. Only the highest assigned
 *                          level per KPI+location is ever removable —
 *                          lower levels render as locked/view-only, since
 *                          the sequential rule blocks removing them until
 *                          the level(s) above are gone. Removable rows
 *                          support individual Remove, multi-select, and
 *                          Select All / Remove All.
 *
 * The KPI-card layout and design tokens are unchanged from the original
 * drawer — nothing here converts it to a table.
 */

import { useState, useMemo, useCallback } from "react";
import { apiCall } from "../../../../../../_services/apiCall";
import config from "../../../../../../config/config";
import TagFilterBar from "../../../../../Settings/Components/TagFilterBar";
import { questionMatchesSteps } from "../../../../../Settings/Components/TagFilterUtils";
import MultiSelectDropdown from "../../../../../../globalComponents/form/dropdowns/MultiSelectDropdown";
import { slotAuditorIds } from "./auditorSlots";


/* ════════════════════════════════════════════════
   DESIGN TOKENS (identical to BulkAuditAssignModal.jsx)
════════════════════════════════════════════════ */
const T = {
  blue: "#2563eb", blueL: "#eff6ff", blueB: "#bfdbfe",
  purple: "#7c3aed", purpleL: "#faf5ff", purpleB: "#ddd6fe",
  green: "#15803d", greenL: "#f0fdf4", greenB: "#bbf7d0",
  amber: "#b45309", amberL: "#fffbeb", amberB: "#fde68a",
  red: "#be123c", redL: "#fff1f2", redB: "#fecdd3",
  ink: "#0f172a", ink2: "#334155", ink3: "#64748b", ink4: "#94a3b8",
  rule: "#e2e8f0", rule2: "#f1f5f9", bg: "#f8fafc",
  white: "#ffffff",
};

/* ════════════════════════════════════════════════
   VALIDATION HELPERS
   (mirrored server-side — see assignment-validation.util.ts)
════════════════════════════════════════════════ */

/** Levels currently assigned for a given KPI at a given location. */
const getAssignedLevels = (grouped, locId, qid) => {
  const locData = grouped?.[String(locId)] || {};
  return Object.entries(locData)
    .filter(([, slot]) => (slot.questionIds || []).map(Number).includes(Number(qid)))
    .map(([lv]) => Number(lv));
};


/**
 * Sequential removal rule, generalized for unlimited levels:
 *  - A level can be removed only if no higher level is currently assigned.
 *  - Never shifts remaining levels — this only decides whether the
 *    targeted level may be deleted as-is.
 */
const validateLevelRemoval = (assignedLevels, level) => {
  const higherLevels = assignedLevels.filter((lv) => lv > level);
  if (higherLevels.length === 0) return { allowed: true };

  const maxHigher = Math.max(...higherLevels);
  return {
    allowed: false,
    reason: `Cannot remove Level ${level} because Level ${maxHigher} exists. Please remove higher levels first.`,
  };
};

/* ════════════════════════════════════════════════
   SMALL ATOMS
════════════════════════════════════════════════ */
const Spin = ({ size = 12, color = T.blue }) => (
  <span style={{ display: "inline-block", width: size, height: size, flexShrink: 0, border: `2px solid ${color}22`, borderTopColor: color, borderRadius: "50%", animation: "kd-spin .6s linear infinite" }} />
);

/* ════════════════════════════════════════════════
   KPI DRAWER
════════════════════════════════════════════════ */
const AuditorAssignedKpisDrawer = ({
  qIds,
  entries,
  questionTitles,
  existingGrouped,
  allLocations,
  allAuditors,
  selectedLocIds,
  groupLabel,
  onClose,
  tabular,
  financialYearId,
  auditorType,
  reportingQuestions,
  mode = "assignments", // "unassigned" | "assignments"
  levelContext, // the level this drawer was opened from (group.nextLevel)
  onRefreshAssignments, // parent's fetchExistingConfig — re-fetches getExistingAssignmentConfig and updates existingGrouped
}) => {
  const isReadOnly = mode === "unassigned";

  const [search, setSearch] = useState("");
  const [userFilter, setUserFilter] = useState([]);
  const [removalError, setRemovalError] = useState("");
  const [removingKeys, setRemovingKeys] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [filterSteps, setFilterSteps] = useState([]);

  // Only offer locations that are actually relevant to this group —
  // Chennai shouldn't even be considered when opened from a Bangalore-only group.
  const relevantLocIds = useMemo(() => {
    if (tabular) return selectedLocIds;
    if (entries?.length) return [...new Set(entries.flatMap((e) => e.locIds))];
    return selectedLocIds;
  }, [entries, tabular, selectedLocIds]);

  // Non-tabular assignment is always scoped to a single location, so there's
  // never a real choice to make here — just use it directly.
  const activeLocId = relevantLocIds[0];

  const allQuestions = useMemo(() => reportingQuestions ?? [], [reportingQuestions]);

  // qid → raw tag-combo data, for the cascade tag filter below
  const tagsByQid = useMemo(() => {
    const map = {};
    allQuestions.forEach((q) => { map[q.questionId] = q.tags; });
    return map;
  }, [allQuestions]);

  // Tag filter options should reflect only the KPIs actually listed in this
  // drawer (this group's unassigned/assignment qIds) — not the full KPI catalog.
  const filterableQuestions = useMemo(() => {
    const idSet = new Set(qIds.map(String));
    return allQuestions.filter((q) => idSet.has(String(q.questionId)));
  }, [allQuestions, qIds]);

  const getName = useCallback((id) => allAuditors.find((a) => String(a.id) === String(id))?.name || `Auditor #${id}`, [allAuditors]);
  const getLocName = useCallback((id) => allLocations.find((l) => l.id === id)?.unitCode || `Loc #${id}`, [allLocations]);

  // Multi-location handling: never mix locations — always look at exactly one.
  const effectiveLocIds = tabular ? selectedLocIds : [activeLocId];

  // Per-qid effective locations: intersect the entity's own locIds with whatever
  // the location dropdown is currently narrowed to.
  const effectiveLocIdsForQid = useCallback((qid) => {
    if (tabular) return selectedLocIds;
    const scoped = entries?.length
      ? (entries.find((e) => String(e.qid) === String(qid))?.locIds || [])
      : relevantLocIds;
    return activeLocId ? scoped.filter((l) => l === activeLocId) : scoped;
  }, [tabular, entries, relevantLocIds, activeLocId, selectedLocIds]);

  const assignmentsByQid = useMemo(() => {
    if (isReadOnly) return {};
    const map = {};
    (entries || []).forEach(({ qid, locIds }) => {
      const rows = [];
      const pushSlotRows = (lid, lv, slot, isRemovable) => {
        slotAuditorIds(slot).forEach((auditorId) => {
          rows.push({
            locId: lid,
            locName: getLocName(lid),
            level: lv,
            auditorId,
            auditorName: getName(auditorId),
            key: `${qid}-${lid}-${lv}-${auditorId}`,
            isRemovable,
          });
        });
      };
      locIds.forEach((lid) => {
        // The current (removable) level for this qid+loc — one row per auditor
        const slot = (existingGrouped?.[String(lid)] || {})[String(levelContext)];
        if (slot) pushSlotRows(lid, levelContext, slot, true);
        // Everything below it — same flat list, just locked
        getAssignedLevels(existingGrouped, lid, qid)
          .filter((lv) => lv < levelContext)
          .sort((a, b) => a - b)
          .forEach((lv) => {
            const belowSlot = (existingGrouped?.[String(lid)] || {})[String(lv)];
            pushSlotRows(lid, lv, belowSlot, false);
          });
      });
      // Keep it readable: group by location, level ascending within each location
      rows.sort((a, b) => a.locId - b.locId || a.level - b.level);
      map[qid] = rows;
    });
    return map;
  }, [isReadOnly, entries, levelContext, existingGrouped, getLocName, getName]);

  /* Search + KPI tag filter + user tag filter — all combine together */
  const filteredQIds = useMemo(() => {
    return qIds.filter((qid) => {
      const title = (questionTitles[qid] || `KPI #${qid}`).toLowerCase();
      if (search && !title.includes(search.toLowerCase())) return false;
      if (filterSteps.length && !questionMatchesSteps({ tags: tagsByQid[qid], questionId: qid }, filterSteps)) return false;

      if (!isReadOnly && userFilter.length) {
        const rows = assignmentsByQid[qid] || [];
        if (!rows.some((r) => userFilter.includes(String(r.auditorId)))) return false;
      }
      return true;
    });
  }, [qIds, questionTitles, search, filterSteps, tagsByQid, userFilter, effectiveLocIds, existingGrouped, isReadOnly]);

  const removableInView = useMemo(() => {
    if (isReadOnly) return [];

    const list = [];

    filteredQIds.forEach((qid) => {
      (assignmentsByQid[qid] || [])
        .filter((a) => a.isRemovable)
        .forEach((a) => list.push({ qid, ...a }));
    });

    return list;
  }, [isReadOnly, filteredQIds, assignmentsByQid]);

  /* Filter options, derived from the KPIs/assignments currently in view.
     Not needed in read-only mode — nothing is assigned yet to filter by user. */
  const userOptions = useMemo(() => {
    if (isReadOnly) return [];
    const ids = new Set();
    Object.values(assignmentsByQid).forEach((rows) => rows.forEach((r) => ids.add(String(r.auditorId))));
    return [...ids].map((id) => ({ value: id, label: getName(id) }));
  }, [isReadOnly, assignmentsByQid, getName]);

  const allRemovableSelected = removableInView.length > 0 && removableInView.every((r) => selectedKeys.has(r.key));

  const toggleSelect = useCallback((key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const handleSelectAllToggle = useCallback(() => {
    setSelectedKeys((prev) => {
      const allCurrentlySelected = removableInView.length > 0 && removableInView.every((r) => prev.has(r.key));
      return allCurrentlySelected ? new Set() : new Set(removableInView.map((r) => r.key));
    });
  }, [removableInView]);

  /* Remove one or more assignments in one batch. Every item is re-validated
     against the sequential rule right before acting — items should already
     be "removable" (top-level) by construction, but this stays defense-in-depth,
     matching the same check the backend performs. */
  const performRemovals = useCallback(async (items) => {
    if (!items.length) return;

    const invalidItem = items.find((item) => {
      const assignedLevels = getAssignedLevels(existingGrouped, item.locId, item.qid);
      return !validateLevelRemoval(assignedLevels, item.level).allowed;
    });
    if (invalidItem) {
      const assignedLevels = getAssignedLevels(existingGrouped, invalidItem.locId, invalidItem.qid);
      setRemovalError(validateLevelRemoval(assignedLevels, invalidItem.level).reason);
      return;
    }

    setRemovalError("");
    setRemovingKeys(new Set(items.map((it) => it.key)));
    setBulkBusy(items.length > 1);
    try {
      const byAuditor = new Map();
      items.forEach((it) => {
        const key = String(it.auditorId);
        if (!byAuditor.has(key)) byAuditor.set(key, { auditorId: it.auditorId, qids: new Set() });
        byAuditor.get(key).qids.add(it.qid);
      });
      for (const { auditorId, qids } of byAuditor.values()) {
        await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}removeAuditorsFromQuestions`, {},
          {
            financialYearId,
            questionIds: [...qids],
            ...(tabular ? {} : { locationId: Number(activeLocId) }),
            auditorType,
            level: levelContext,
            ...(auditorId != null ? { auditorIds: [Number(auditorId)] } : {}),
          },
          "POST"
        );
      }
      // Re-run the same getExistingAssignmentConfig fetch the parent uses on open —
      // this updates the parent's existingGrouped, which flows back down as a prop.
      await onRefreshAssignments?.();
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        items.forEach((it) => next.delete(it.key));
        return next;
      });
    } catch (e) {
      setRemovalError(e?.message || "Failed to remove assignment(s).");
    } finally {
      setRemovingKeys(new Set());
      setBulkBusy(false);
    }
  }, [existingGrouped, financialYearId, auditorType, onRefreshAssignments]);

  const handleRemoveSelected = () => {
    performRemovals(removableInView.filter((r) => selectedKeys.has(r.key)));
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1400, display: "flex", alignItems: "stretch" }}>
      <style>{`@keyframes kd-spin { to { transform: rotate(360deg); } }`}</style>
      <div onClick={onClose} style={{ flex: 1, background: "rgba(0,0,0,.35)", cursor: "pointer" }} />

      <div style={{ width: 540, maxWidth: "95vw", background: T.white, display: "flex", flexDirection: "column", boxShadow: "-8px 0 40px rgba(0,0,0,.18)" }}>

        {/* Header */}
        <div style={{ padding: "16px 18px 13px", borderBottom: `1px solid ${T.rule}`, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.ink }}>{groupLabel}</div>
              <div style={{ fontSize: 11, color: T.ink3, marginTop: 2 }}>
                {filteredQIds.length} of {qIds.length} KPI{qIds.length !== 1 ? "s" : ""} shown
                {isReadOnly ? " · Read-only" : ""}
              </div>
            </div>
            <button onClick={onClose} style={{ border: `1px solid ${T.rule}`, borderRadius: 8, background: T.bg, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 15, color: T.ink3 }}>×</button>
          </div>

          {/* Search */}
          <div style={{ marginTop: 10, position: "relative" }}>
            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: T.ink4 }}>🔍</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search questions…"
              style={{ width: "100%", padding: "7px 10px 7px 30px", fontSize: 12, border: `1px solid ${T.rule}`, borderRadius: 7, background: T.bg, color: T.ink2, boxSizing: "border-box" }}
            />
          </div>

          {/* KPI tag filter (both modes) + Users filter (assignments mode only) — same row */}
          <div style={{ marginTop: 8, display: "flex", gap: 7, alignItems: "flex-start" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <TagFilterBar
                allQuestions={filterableQuestions}
                filterSteps={filterSteps}
                onFilterStepsChange={setFilterSteps}
                pendingIds={new Set(filteredQIds)}
              />
            </div>

            {!isReadOnly && (
              <div style={{ width: 200, flexShrink: 0 }}>
                <MultiSelectDropdown
                  options={userOptions}
                  selected={userFilter}
                  onChange={setUserFilter}
                  placeholder="Filter by user…"
                  showSelectAll={false}
                />
              </div>
            )}
          </div>
        </div>

        {!isReadOnly && removalError && (
          <div style={{ margin: "10px 14px 0", padding: "8px 12px", borderRadius: 7, background: T.redL, border: `1px solid ${T.redB}`, color: T.red, fontSize: 11.5, display: "flex", gap: 7 }}>
            <span>⚠</span><span>{removalError}</span>
          </div>
        )}

        {/* Bulk actions — only for removable (top-level) assignments in the current view */}
        {!isReadOnly && removableInView.length > 0 && (
          <div style={{ margin: "10px 14px 0", padding: "7px 11px", borderRadius: 7, background: T.bg, border: `1px solid ${T.rule}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, color: T.ink2, cursor: "pointer" }}>
              <input type="checkbox" checked={allRemovableSelected} onChange={handleSelectAllToggle} style={{ accentColor: T.blue, width: 13, height: 13 }} />
              Select all removable ({removableInView.length})
            </label>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={handleRemoveSelected}
                disabled={selectedKeys.size === 0 || bulkBusy}
                style={{ fontSize: 11, fontWeight: 700, color: selectedKeys.size ? T.red : T.ink4, background: T.white, border: `1.5px solid ${selectedKeys.size ? T.redB : T.rule}`, borderRadius: 7, padding: "5px 10px", cursor: selectedKeys.size && !bulkBusy ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 5 }}
              >
                {bulkBusy && selectedKeys.size > 0 && <Spin size={10} color={T.red} />}
                Remove Selected{selectedKeys.size > 0 ? ` (${selectedKeys.size})` : ""}
              </button>
            </div>
          </div>
        )}

        {/* KPI list — unchanged card layout, never a flat table */}
        <div style={{ flex: 1, overflowY: "auto", padding: "10px 14px" }}>
          {filteredQIds.length === 0 && (
            <div style={{ textAlign: "center", color: T.ink4, fontSize: 12, padding: "30px 0" }}>No KPIs match your filters.</div>
          )}

          {filteredQIds.map((qid, i) => {
            const title = questionTitles[qid] || `KPI #${qid}`;

            /* Read-only mode: a plain, filterable list of KPIs still needing this level.
               Prior-level assignments (if any) are shown as small non-interactive chips
               for context only — never a full removable block, so this stays visually
               distinct from the "Assignments" mode below. */
            if (isReadOnly) {
              const priorAssignments = [];
              effectiveLocIdsForQid(qid).forEach((lid) => {
                getAssignedLevels(existingGrouped, lid, qid).forEach((level) => {
                  const slot = (existingGrouped?.[String(lid)] || {})[String(level)];
                  slotAuditorIds(slot).forEach((auditorId) => {
                    priorAssignments.push({ locId: lid, locName: getLocName(lid), level, auditorName: getName(auditorId) });
                  });
                });
              });
              priorAssignments.sort((a, b) => a.level - b.level);

              return (
                <div key={qid} style={{ padding: "11px 13px", borderRadius: 9, border: `1px solid ${T.rule}`, marginBottom: 8, background: i % 2 === 0 ? T.white : T.bg }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ minWidth: 22, height: 22, borderRadius: "50%", background: T.amber, color: T.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, flexShrink: 0 }}>
                      L{levelContext}
                    </div>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, lineHeight: 1.45, flex: 1 }}>{title}</div>
                    <span style={{ fontSize: 10, color: T.amber, background: T.amberL, padding: "2px 8px", borderRadius: 20, border: `1px solid ${T.amberB}`, whiteSpace: "nowrap", flexShrink: 0 }}>
                      Needs assignment
                    </span>
                  </div>

                  {priorAssignments.length > 0 && (
                    <div style={{ marginTop: 7, paddingLeft: 32, display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {priorAssignments.map((p, pi) => (
                        <span key={pi} style={{ fontSize: 10, fontWeight: 600, color: T.ink3, background: T.bg, padding: "2px 8px", borderRadius: 20, border: `1px solid ${T.rule}`, whiteSpace: "nowrap" }}>
                          L{p.level} · {p.auditorName}{!tabular ? ` · ${p.locName}` : ""}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            /* Assignments mode: only the top assigned level per KPI+location is
               removable; everything beneath it is locked (view-only) until the
               level(s) above it are removed first. */
            const assignments = assignmentsByQid[qid] || [];

            return (
              <div key={qid} style={{ padding: "11px 13px", borderRadius: 9, border: `1px solid ${T.rule}`, marginBottom: 8, background: i % 2 === 0 ? T.white : T.bg }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, lineHeight: 1.45, marginBottom: 7 }}>{title}</div>

                {assignments.length === 0 ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: T.green, fontWeight: 600 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: T.green, flexShrink: 0 }} />
                    No existing assignments — will be assigned fresh
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: T.ink4, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 5 }}>
                      Current assignments
                    </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {assignments.map((a) => {
                          const isRemoving = removingKeys.has(a.key);
                          const isSelected = selectedKeys.has(a.key);
                          return (
                            <div key={a.key} style={{ display: "flex", alignItems: "center", gap: 7, padding: "5px 9px", borderRadius: 6, background: a.isRemovable ? T.blueL : T.bg, border: `1px solid ${a.isRemovable ? T.blueB : T.rule}` }}>
                              {a.isRemovable ? (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleSelect(a.key)}
                                  style={{ accentColor: T.blue, width: 12, height: 12, flexShrink: 0 }}
                                />
                              ) : (
                                <span style={{ width: 12, flexShrink: 0 }} />
                              )}
                              <div style={{ minWidth: 22, height: 22, borderRadius: "50%", background: a.isRemovable ? T.blue : T.ink4, color: T.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, flexShrink: 0 }}>
                                L{a.level}
                              </div>
                              <span style={{ fontSize: 11.5, fontWeight: 600, color: T.ink2, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {a.auditorName}
                              </span>
                              {!tabular && (
                                <span style={{ fontSize: 10, color: T.ink3, background: T.white, padding: "1px 7px", borderRadius: 20, border: `1px solid ${T.rule}`, whiteSpace: "nowrap", flexShrink: 0 }}>
                                  📍 {a.locName}
                                </span>
                              )}
                              {a.isRemovable ? (
                                <button
                                  onClick={() => performRemovals([{ qid, ...a }])}
                                  disabled={isRemoving || bulkBusy}
                                  style={{ border: `1px solid ${T.redB}`, background: T.white, color: T.red, borderRadius: 6, padding: "3px 8px", fontSize: 10, fontWeight: 700, cursor: (isRemoving || bulkBusy) ? "not-allowed" : "pointer", flexShrink: 0, display: "flex", alignItems: "center", gap: 4 }}
                                >
                                  {isRemoving ? <Spin size={9} color={T.red} /> : "Remove"}
                                </button>
                              ) : (
                                <span style={{ fontSize: 10, color: T.ink4, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0, display: "flex", alignItems: "center", gap: 3 }}>
                                  🔒 View only
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ padding: "10px 18px", borderTop: `1px solid ${T.rule}`, flexShrink: 0, background: T.bg }}>
          <button onClick={onClose} style={{ width: "100%", padding: "8px 0", borderRadius: 8, border: `1.5px solid ${T.rule}`, background: T.white, color: T.ink2, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            ← Back to Assignment
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditorAssignedKpisDrawer;